import type { BaselineKind, Constraints, Coverage, Distribution, Measure, RuleGroup } from "@trackmark/core";

/**
 * The one place the client talks to the server.
 *
 * Everything goes through here so that three things are true everywhere rather than per screen:
 * an HTTP error carries its status (the login screen needs to tell 401 from 429), a 401 anywhere
 * means the session went and the app should say so rather than rendering an empty library, and
 * nothing silently succeeds on a response nobody checked.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Fired on any 401 so the shell can fall back to the login screen from wherever it was. */
export const UNAUTHENTICATED = "trackmark:unauthenticated";

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // A network failure is not a 500 and must not read like one — the interface says "the server
    // didn't respond", which is what actually happened.
    throw new ApiError(0, "offline", "THE SERVER DIDN'T RESPOND. CHECK THE CONNECTION AND TRY AGAIN.");
  }

  if (response.status === 401) {
    globalThis.dispatchEvent?.(new CustomEvent(UNAUTHENTICATED));
    throw new ApiError(401, "unauthenticated", "Your session ended. Log in again.");
  }

  if (!response.ok) {
    const detail = (await response.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new ApiError(
      response.status,
      detail.error ?? "error",
      detail.detail ?? detail.error ?? `the server returned ${response.status}`,
    );
  }

  // 204 and empty bodies are legitimate answers to a PUT.
  const text = await response.text();
  return (text === "" ? undefined : JSON.parse(text)) as T;
}

export const api = {
  get: <T>(url: string) => request<T>("GET", url),
  post: <T>(url: string, body?: unknown) => request<T>("POST", url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>("PUT", url, body ?? {}),
  del: <T>(url: string, body?: unknown) => request<T>("DELETE", url, body ?? {}),
};

// ---------------------------------------------------------------- typed endpoints

export interface Counts {
  songs: number;
  albums: number;
  artists: number;
  rated: number;
  needToRate: number;
  incompleteAlbums: number;
  claimed: number;
  favourites: number;
  /** Recording groups the app could not settle on its own. */
  needsTriage: number;
}

export const endpoints = {
  me: () => api.get<{ authenticated: true }>("/api/auth/me"),
  login: (username: string, password: string) => api.post<{ ok: true }>("/api/auth/login", { username, password }),
  /**
   * Sets the username, gated on the current password. No screen calls it — it is the recovery
   * path for an account that predates the field, reachable with curl and a session cookie.
   */
  setUsername: (username: string, password: string) =>
    api.post<{ username: string }>("/api/auth/username", { username, password }),
  logout: () => api.post<{ ok: true }>("/api/auth/logout"),

  counts: () => api.get<Counts>("/api/counts"),

  queue: (strategy = "oldest", limit = 200) =>
    api.get<{ strategy: string; songs: QueueSong[] }>(`/api/queue?strategy=${strategy}&limit=${limit}`),

  album: (id: number) => api.get<AlbumPayload>(`/api/albums/${id}`),
  cohesion: (id: number, cohesion: number | null) => api.put(`/api/albums/${id}/cohesion`, { cohesion }),

  lists: () => api.get<{ lists: ListPayload[] }>("/api/lists"),
  createList: (name: string, rules: RuleGroup) => api.post<{ id: number }>("/api/lists", { name, rules }),
  updateList: (id: number, name: string, rules: RuleGroup) => api.put(`/api/lists/${id}`, { name, rules }),
  deleteList: (id: number) => api.del(`/api/lists/${id}`),
  listPreview: (rules: RuleGroup, limit = 12) =>
    api.post<{ total: number; hints: { excludes: number }[]; songs: PreviewSongPayload[] }>("/api/lists/preview", {
      rules,
      // Zero would be rejected by the schema, so the generator asks for one row and ignores it —
      // it wants the count and the hints, not the preview.
      limit: Math.max(1, limit),
    }),

  playlists: () => api.get<{ playlists: PlaylistPayload[] }>("/api/playlists"),
  generatePlaylist: (rules: RuleGroup, constraints: Constraints) =>
    api.post<GeneratePayload>("/api/playlists/generate", { rules, constraints }),
  savePlaylist: (name: string, rules: RuleGroup, constraints: Constraints, songIds: number[]) =>
    api.post<{ id: number; tracks: number }>("/api/playlists", { name, rules, constraints, songIds }),
  deletePlaylist: (id: number) => api.del(`/api/playlists/${id}`),

  dashboard: () => api.get<DashboardPayload>("/api/dashboard"),

  /** What the library knows about listening. The import itself posts a file body, not JSON. */
  listeningStatus: () =>
    api.get<{
      plays: number;
      streams: number;
      tracks: number;
      matchedTracks: number;
      songsWithPlays: number;
      firstAt: string | null;
      lastAt: string | null;
    }>("/api/listening/status"),

  genres: () => api.get<{ genres: { id: number; name: string; songCount: number }[] }>("/api/genres"),
  addGenre: (name: string) => api.post<{ id: number; name: string }>("/api/genres", { name }),
  removeGenre: (id: number) => api.del(`/api/genres/${id}`),

  tags: () => api.get<TagQueue>("/api/tags"),
  bulkTags: (tags: string[], genreId: number | null, ignore: boolean) =>
    api.post<{ tags: number; filed: number; ignored: number }>("/api/tags/bulk", { tags, genreId, ignore }),

  /**
   * The artwork backfill: what it has done, and the two levers.
   *
   * A background job with a progress line rather than a modal — the library stays rateable while
   * it fills in, and leaving the screen does not stop it (docs/IMPORT-PROPOSAL.md §5.2).
   */
  artworkProgress: () => api.get<ArtworkProgress>("/api/artwork/progress"),
  startArtwork: () => api.post<ArtworkProgress & { started: true }>("/api/artwork/backfill"),
  stopArtwork: () => api.post<{ stopping: boolean }>("/api/artwork/backfill/stop"),
  /** Puts the albums that came back with no cover back in the queue, for one more look. */
  retryArtwork: () => api.post<{ requeued: number }>("/api/artwork/retry"),

  settings: () => api.get<AppSettings>("/api/settings"),
  setSetting: (key: string, value: number) => api.put<{ key: string; value: number }>("/api/settings", { key, value }),
  lastExport: () => api.get<{ lastExportAt: string | null }>("/api/export/last"),

  statistics: (period: string) => api.get<StatisticsPayload>(`/api/statistics?period=${period}`),

  search: (q: string) => api.get<LibrarySearch>(`/api/search?q=${encodeURIComponent(q)}`),
  searchMusicBrainz: (q: string) =>
    api.get<{ q: string; cached: boolean; results: MbResult[] }>(`/api/search/musicbrainz?q=${encodeURIComponent(q)}`),
  searchRecordings: (q: string) =>
    api.get<{ results: MbRecording[] }>(`/api/search/recordings?q=${encodeURIComponent(q)}`),
  addPreview: (q: { releaseGroupMbid: string; title: string; artist: string; year?: number | null; releaseMbid?: string }) =>
    api.get<AddPreview>(`/api/add/preview?${params({ ...q, year: q.year ?? undefined })}`),
  add: (body: {
    releaseGroupMbid: string;
    releaseMbid: string;
    title: string;
    artist: string;
    year: number | null;
    primaryType: string | null;
    bonus: number[];
  }) => api.post<{ albumId: number; added: number; core: number; bonus: number }>("/api/add", body),

  librarySongs: (q: { sort?: string; unratedOnly?: boolean; ownedOnly?: boolean } = {}) =>
    api.get<{ sort: string; total: number; songs: LibrarySong[] }>(`/api/library/songs?${params(q)}`),
  libraryAlbums: (q: { sort?: string; incompleteOnly?: boolean; ownedOnly?: boolean } = {}) =>
    api.get<{ sort: string; total: number; albums: LibraryAlbum[] }>(`/api/library/albums?${params(q)}`),

  rankingSongs: (asOf?: string | null) =>
    api.get<{ songs: RankedSong[] }>(`/api/rankings/songs${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ""}`),
  /** Resolved server-side: `last-session` is a fact about the event log, not arithmetic. */
  baselines: () => api.get<BaselineList>("/api/rankings/baselines"),
  rankingArtists: () => api.get<ArtistRankings>("/api/rankings/artists"),
  /**
   * Persists a dragged tier. The server writes the minimum and answers with the key every song
   * ended up holding, which is what the table settles on — the optimistic keys the client
   * generated are the same arithmetic, but a rebalance is the server's to decide.
   */
  orderTier: (tier: number, songIds: number[]) =>
    api.put<OrderedTier>(`/api/rankings/tier/${tier}/order`, { songIds }),
  rankingAlbums: (includeCompilations = false) =>
    api.get<{ albums: RankedAlbum[] }>(`/api/rankings/albums?includeCompilations=${includeCompilations}`),
};

/** Only the keys that are set travel, so a default stays the server's to decide. */
const params = (q: Record<string, string | number | boolean | null | undefined>) =>
  new URLSearchParams(
    Object.entries(q)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  ).toString();

export interface LibrarySong {
  id: number;
  title: string;
  artist: string;
  artistId: number;
  album: string;
  albumId: number;
  year: number | null;
  score: number | null;
  addedAt: string | null;
  favourite: boolean;
  isTracked: boolean;
  letter: string;
  art: string | null;
}

export interface LibraryAlbum {
  id: number;
  title: string;
  artist: string;
  year: number | null;
  primaryType: string;
  isTracked: boolean;
  coreCount: number;
  ratedCore: number;
  cohesion: number | null;
  /** When the RECORD arrived — its first song's timestamp, not the row's. */
  addedAt: string | null;
  tracks: { rating: number | null; isCore: boolean; durationMs: number | null }[];
  letter: string;
  art: string | null;
}

export interface ListPayload {
  id: number;
  name: string;
  kind: string;
  rules: RuleGroup;
  itemCount: number;
  updatedAt: string;
}

export interface PreviewSongPayload {
  id: number;
  title: string;
  artist: string;
  album: string;
  albumId: number;
  score: number | null;
}

export interface PlaylistPayload {
  id: number;
  name: string;
  itemCount: number;
  targetCount: number | null;
  updatedAt: string;
}

export interface GeneratePayload {
  requested: number;
  durationMs: number;
  poolSize: number;
  report: { binding: string | null; shortBy: number; relaxed: { name: string; to: number; would: number } | null };
  sentence: string;
  tracks: {
    id: number;
    title: string;
    artist: string;
    album: string;
    albumId: number;
    score: number;
    durationMs: number | null;
  }[];
}

export interface DashboardSong {
  id: number;
  title: string;
  artist: string;
  /** The row prints the artist's name, so it carries the way to reach them. */
  artistId: number;
  album: string;
  albumId: number;
  score: number;
  ratedAt?: string;
  art: string | null;
}

export interface DashboardPayload {
  queue: { needToRate: number; ratedThisWeek: number; longestSitting: number };
  recent: DashboardSong[];
  top: DashboardSong[];
  counters: { songsRated: number; albumsComplete: number; qualifiedArtists: number; changesThisMonth: number };
}

export interface ArtworkProgress {
  /** Every album, whether or not anybody has looked for its cover. */
  total: number;
  stored: number;
  /** Looked for and not archived. Settled, not pending — the fallback tile is the answer. */
  unavailable: number;
  pending: number;
  /** Two MusicBrainz requests per album at one a second; null when nothing is pending. */
  etaSeconds: number | null;
  running: boolean;
  /** Whether this deploy has an R2 bucket to write to at all. */
  configured: boolean;
  /** MusicBrainz is refusing us and the queue is holding off, rather than the job being stuck. */
  throttled: boolean;
}

export interface TagQueue {
  tags: { tag: string; songCount: number; genreId: number | null; genre: string | null; ignored: boolean }[];
  occurrences: { decided: number; total: number; waiting: number };
}

export interface AppSettings {
  shrinkageM: number;
  artistThreshold: number;
  priorFixed: number;
  priorMinRated: number;
}

export interface StatisticsPayload {
  period: string;
  since: string | null;
  distribution: Distribution;
  byDecade: Measure[];
  byGenre: Measure[];
  coverage: Coverage;
}

export interface LibrarySearch {
  q: string;
  songs: { id: number; title: string; artist: string; album: string; albumId: number; trackNo: number | null; score: number | null }[];
  albums: { id: number; title: string; artist: string; year: number | null; isTracked: boolean; coreCount: number; ratedCore: number }[];
  artists: { id: number; name: string; songCount: number; letter: string }[];
}

export interface MbResult {
  mbid: string;
  title: string;
  artist: string;
  primaryType: string | null;
  firstReleaseDate: string | null;
  disambiguation: string | null;
  score: number;
}

export interface MbRecording {
  mbid: string;
  title: string;
  artist: string;
  lengthMs: number | null;
  releases: string[];
  disambiguation: string | null;
  score: number;
}

export interface AddPreview {
  releaseGroupMbid: string;
  title: string;
  artist: string;
  alreadyInLibrary: { id: number; title: string } | null;
  needsChoice: boolean;
  chosenReleaseMbid: string;
  releases: { mbid: string; title: string; date: string | null; trackCount: number; status: string | null; score: number; reasons: string[] }[];
  tracks: { position: number; discNo: number; title: string; durationMs: number | null; recordingMbid: string | null }[];
}

export interface BaselineList {
  lastSessionStart: string | null;
  baselines: { kind: BaselineKind; label: string; at: string | null }[];
}

export interface QueueSong {
  id: number;
  title: string;
  artist: string;
  album: string;
  albumId: number;
  year: number | null;
  genre: string | null;
  durationMs: number | null;
  skipCount: number;
  isCore: boolean;
  art: string | null;
  rating: number | null;
  ratedAt: string | null;
}

export interface AlbumPayload {
  id: number;
  title: string;
  artist: string;
  year: number | null;
  label: string | null;
  genre: string | null;
  art: string | null;
  cohesion: number | null;
  isTracked: boolean;
  claimed: boolean;
  primaryType: string;
  tracks: {
    id: number;
    no: number | null;
    discNo: number;
    title: string;
    durationMs: number | null;
    isCore: boolean;
    favourite: boolean;
    rating: number | null;
    linkedTo: number | null;
    alsoOn: number;
    sameAs: { title: string; rating: number } | null;
  }[];
  editions: { id: number; name: string; trackCount: number | null; isPreferred: boolean }[];
}

export interface RankedSong {
  id: number;
  title: string;
  artist: string;
  artistId: number;
  album: string;
  albumId: number;
  year: number | null;
  score: number | null;
  ratedAt: string | null;
  art: string | null;
  /** Position within the tier. null = unplaced: unmeasured, never worst. */
  orderKey: string | null;
  /** Rank at the chosen baseline. Absent without one — there is no MOVE column until asked for. */
  previousRank?: number | null;
  isNew?: boolean;
}

export interface OrderedTier {
  tier: number;
  /** How many rows actually changed key. One, for a drag inside an already-ordered group. */
  written: number;
  rebalanced: boolean;
  orderKeys: Record<number, string>;
}

export interface RankedAlbum {
  id: number;
  title: string;
  artist: string;
  year: number | null;
  primaryType: string;
  genre: string | null;
  cohesion: number | null;
  art: string | null;
  coreCount: number;
  ratedCore: number;
  tracks: { rating: number | null; isCore: boolean; durationMs: number | null }[];
}

export interface ArtistRankings {
  collection: { totalRated: number; mean: number | null };
  settings: { m: number; artistThreshold: number; priorFixed: number; priorMinRated: number };
  artists: { id: number; name: string; ratedCount: number; raw: number | null; ownedCount: number; albumCount: number }[];
}
